import { ComponentPropsWithoutRef, forwardRef } from 'react';
import GWPSkeletonBlock from '../GWPSkeletonBlock';
import StickyHeaderTable from './StickyHeaderTable';

export interface GWPTableSkeletonProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
	columns?: number;
	rows?: number;
	label?: string;
}

const GWPTableSkeleton = forwardRef<HTMLDivElement, GWPTableSkeletonProps>(function GWPTableSkeleton(
	{ className, columns = 5, rows = 8, label = 'Loading table…', ...props },
	ref,
) {
	const columnCount = normaliseCount(columns, 5);
	const rowCount = normaliseCount(rows, 8);
	const rootClassName = className ? `gwp-table-skeleton ${className}` : 'gwp-table-skeleton';

	return (
		<div ref={ref} className={rootClassName} role="status" aria-live="polite" aria-busy="true" {...props}>
			<span className="gwp-table-skeleton-announcement">{label}</span>
			<div className="gwp-table-skeleton-scroll" aria-hidden="true">
				<StickyHeaderTable variant="data">
					<thead>
						<tr>
							{Array.from({ length: columnCount }, (_, columnIndex) => (
								<th key={columnIndex}>
									<GWPSkeletonBlock width={columnIndex === 0 ? '72%' : '58%'} />
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{Array.from({ length: rowCount }, (_, rowIndex) => (
							<tr key={rowIndex}>
								{Array.from({ length: columnCount }, (__, columnIndex) => (
									<td key={columnIndex}>
										<GWPSkeletonBlock width={`${52 + ((rowIndex + columnIndex) % 4) * 10}%`} />
									</td>
								))}
							</tr>
						))}
					</tbody>
				</StickyHeaderTable>
			</div>
		</div>
	);
});

GWPTableSkeleton.displayName = 'GWPTableSkeleton';

function normaliseCount(value: number, fallback: number) {
	return Number.isFinite(value) ? Math.max(1, Math.floor(value)) : fallback;
}

export default GWPTableSkeleton;
